import { useEffect, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtEur } from "../lib/utils";
import { CreditCard, ArrowRight, Lightning, ShieldCheck, Wallet } from "@phosphor-icons/react";
import { toast } from "sonner";
import ClientAutoRecharge from "./ClientAutoRecharge";
import PageHeader from "../components/PageHeader";

const PRESETS = [100, 250, 500, 750];
const MIN_AMOUNT = 100;

export default function ClientRecharge() {
  const { t } = useI18n();
  const [tab, setTab] = useState("topup");
  const [amount, setAmount] = useState(100);
  const [loading, setLoading] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get("/client/wallet").then(({ data }) => setWallet(data));
    api.get("/client/me").then(({ data }) => setProfile(data)).catch(() => {});
  }, []);

  // Fiscal breakdown: IT client → 22% inside total; foreign business (with VAT) → 0%
  const isForeignBusiness = (() => {
    const c = profile?.client;
    if (!c) return false;
    const country = (c.country || "IT").toUpperCase();
    return country !== "IT" && !!c.vat_number;
  })();
  const vatRate = isForeignBusiness ? 0 : 22;
  const imponibile = amount ? +(amount / (1 + vatRate / 100)).toFixed(2) : 0;
  const vatAmount = amount ? +(amount - imponibile).toFixed(2) : 0;

  const stripePay = async () => {
    if (!amount || amount < MIN_AMOUNT) { toast.error(t("rc_min_amount_toast", { min: fmtEur(MIN_AMOUNT) })); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout/session", {
        amount: parseFloat(amount),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("rc_stripe_error"));
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="client-recharge">
      <PageHeader
        eyebrow={t("wallet")}
        title={t("rc_page_title")}
        subtitle={wallet ? t("rc_page_subtitle_balance", { balance: fmtEur(wallet.balance) }) : t("rc_page_subtitle_default")}
        chip="tf-chip-mint"
        icon={Wallet}
      />

      <div className="tf-card overflow-hidden">
        <div className="flex gap-1 px-5 pt-4 border-b border-[var(--tf-border)] bg-[var(--tf-bg)]/40">
          <TabButton active={tab === "topup"} onClick={() => setTab("topup")} icon={CreditCard} label={t("rc_tab_manual")} testid="tab-topup" />
          <TabButton active={tab === "auto"} onClick={() => setTab("auto")} icon={Lightning} label={t("rc_tab_auto")} testid="tab-auto" />
        </div>

        <div className="p-5">
          {tab === "topup" ? (
            <TopupSection
              amount={amount}
              setAmount={setAmount}
              loading={loading}
              onPay={stripePay}
              vatRate={vatRate}
              imponibile={imponibile}
              vatAmount={vatAmount}
              isForeignBusiness={isForeignBusiness}
              t={t}
            />
          ) : (
            <div className="-mx-5 -mb-5">
              <ClientAutoRecharge embedded />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2.5 font-display font-bold text-sm border-b-2 transition-colors -mb-px ${
        active
          ? "border-[var(--tf-primary)] text-[var(--tf-ink)] bg-white rounded-t-lg"
          : "border-transparent text-[var(--tf-muted)] hover:text-[var(--tf-ink)]"
      }`}
    >
      <Icon size={16} weight="bold" />
      {label}
    </button>
  );
}

function TopupSection({ amount, setAmount, loading, onPay, vatRate, imponibile, vatAmount, isForeignBusiness, t }) {
  return (
    <div className="space-y-5">
      <div>
        <div className="tf-label flex items-center gap-2"><CreditCard size={12} weight="bold" /> {t("rc_onetime_title")}</div>
        <p className="text-xs text-[var(--tf-muted)] mt-1.5 max-w-2xl">
          {t("rc_onetime_desc", { min: fmtEur(MIN_AMOUNT) })}
        </p>
      </div>

      <div className="tf-card p-5">
        <div className="tf-label mb-3">{t("rc_choose_amount")}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {PRESETS.map((p) => (
            <button
              key={p}
              onClick={() => setAmount(p)}
              data-testid={`preset-${p}`}
              className={`p-4 rounded-lg border-2 transition-all text-center ${
                amount === p
                  ? "border-[var(--tf-primary)] bg-[var(--tf-primary)]/15"
                  : "border-[var(--tf-border)] hover:border-[var(--tf-border-strong)] hover:bg-[var(--tf-bg)]"
              }`}
            >
              <div className="font-display font-black text-2xl tf-numeric">{fmtEur(p)}</div>
            </button>
          ))}
        </div>

        <div className="mt-5">
          <div className="tf-label mb-2">{t("rc_custom_amount")}</div>
          <div className="flex items-center gap-2">
            <span className="text-[var(--tf-muted)] font-display font-bold text-2xl">€</span>
            <input
              type="number"
              min={MIN_AMOUNT}
              step="50"
              value={amount}
              onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
              className="tf-input tf-numeric text-2xl font-bold !py-3"
              data-testid="custom-amount"
            />
          </div>
          {amount > 0 && amount < MIN_AMOUNT && (
            <div className="text-xs text-[var(--tf-error)] mt-2">
              {t("rc_min_amount_error", { min: fmtEur(MIN_AMOUNT) })}
            </div>
          )}
        </div>
      </div>

      {/* Scorporo IVA prima del pagamento */}
      {amount >= MIN_AMOUNT && (
        <div className="tf-card p-5 bg-[#F5F2E8] space-y-3" data-testid="topup-vat-breakdown">
          <div className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold">{t("rc_summary_title")}</div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-[var(--tf-muted)]">{t("rc_taxable")}</span>
            <span className="font-display font-bold tf-numeric text-[var(--tf-ink)]">{fmtEur(imponibile)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-[var(--tf-muted)]">
              {t("rc_vat", { rate: vatRate })}
              {isForeignBusiness && <span className="ml-1 text-[10px] opacity-75">· {t("rc_reverse_charge")}</span>}
            </span>
            <span className="font-display font-bold tf-numeric text-[var(--tf-ink)]">{fmtEur(vatAmount)}</span>
          </div>
          <div className="pt-3 border-t-2 border-[var(--tf-ink)] flex items-end justify-between">
            <span className="text-xs uppercase tracking-widest text-[var(--tf-ink)] font-black">{t("rc_total_to_pay")}</span>
            <span
              className="font-display font-black text-2xl md:text-3xl tf-numeric text-[var(--tf-ink)]"
              style={{ textShadow: "none" }}
            >
              {fmtEur(amount)}
            </span>
          </div>
          <div className="text-[10px] text-[var(--tf-muted)] text-right">
            {t("rc_wallet_credit_note", { amount: fmtEur(amount) })}
          </div>
        </div>
      )}

      <button
        onClick={onPay}
        disabled={loading || !amount || amount < MIN_AMOUNT}
        className="tf-btn tf-btn-primary w-full py-4 text-base"
        data-testid="stripe-pay-btn"
      >
        {loading ? t("rc_redirecting") : <>{t("rc_pay_btn", { amount: fmtEur(amount) })} <ArrowRight size={16} weight="bold" /></>}
      </button>

      <div className="tf-card p-4 bg-[var(--tf-bg)]/40 flex items-center gap-3 text-xs text-[var(--tf-muted)]">
        <ShieldCheck size={20} weight="duotone" className="text-[var(--tf-ink)] shrink-0" />
        <div>
          {t("rc_secure_note")}
        </div>
      </div>
    </div>
  );
}
